import styled from "@emotion/styled";
import { Box, Stack } from "@mui/joy";
import { InfiniteData } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { subYears } from "date-fns";
import { ApiRouteDetail, PaginatedResponse } from "griptonite-api-client";
import { memo, useEffect, useMemo, useState } from "react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { useDebounce } from "use-debounce";

import { apiFacetsToRoutesFacets } from "@/components/Archive/apiFacetsToRouteFacets";
import ArchiveGraphs from "@/components/Archive/ArchiveGraphs";
import { ArchiveQueryProvider } from "@/components/Archive/ArchiveRoutesQueryContext";
import ArchiveRoutesSidebar from "@/components/Archive/ArchiveRoutesSidebar";
import ArchiveRoutesTable from "@/components/Archive/ArchiveRoutesTable";
import { ArchiveUiStoreProvider, useArchiveUiStore } from "@/components/Archive/ArchiveUiStore";
import { convertArchiveSortFilterParamsToBackend } from "@/components/Archive/convertArchiveSortFilterParamsToBackend";
import { OrientationModal } from "@/components/OrientationModal";
import { ArchiveFilterSidebar } from "@/components/RoutesTable/FilterSidebar/FilterSidebar";
import {
  RouteFacetsProvider,
  RoutesTableStoreProvider,
  useRoutesTableStore,
} from "@/components/RoutesTable/RoutesProvider2";
import {
  useVenueArchiveRoutesByClimbTypeQuery,
  useVenueArchiveRoutesFacetsQuery,
} from "@/hooks/routes";
import { useCurrentClimbType, useCurrentVenueUuid } from "@/hooks/venues";

const ArchiveScreen = () => {
  return (
    <ArchiveUiStoreProvider>
      <RoutesTableStoreProvider>
        <ArchiveQuery>
          <ArchiveFacetsProvider>
            <ArchiveScreenContent />
          </ArchiveFacetsProvider>
        </ArchiveQuery>
      </RoutesTableStoreProvider>
    </ArchiveUiStoreProvider>
  );
};

export const Route = createFileRoute("/gym/$venueUuid/archive/$climbType")({
  component: ArchiveScreen,
  errorComponent: ({ error }) => {
    throw error;
  },
  parseParams: (params) => {
    return {
      climbType: params.climbType.toUpperCase(),
    };
  },
  stringifyParams: (params) => {
    return {
      climbType: params.climbType.toLowerCase(),
    };
  },
});

export const StyledPanelResizeHandleVertical = styled(PanelResizeHandle)`
  height: 0px;
  position: relative;
  &:after {
    content: "";
    position: absolute;
    width: 100%;
    top: 0;
    left: 0;
    transform: translateY(-50%);
    height: 4px;
    z-index: 10;
    opacity: 0;
    background-color: var(--joy-palette-primary-blue1);
    transition: opacity 0.2s;
  }
  &[data-resize-handle-state="hover"]:after,
  &[data-resize-handle-state="drag"]:after {
    opacity: 1;
  }
`;

export const StyledPanelResizeHandleHorizontal = styled(PanelResizeHandle)`
  width: 0px;
  position: relative;
  &:after {
    content: "";
    position: absolute;
    height: 100%;
    top: 0;
    left: 0;
    transform: translateX(-50%);
    width: 4px;
    z-index: 10;
    opacity: 0;
    background-color: var(--joy-palette-primary-blue1);
    transition: opacity 0.2s;
  }
  &[data-resize-handle-state="hover"]:after,
  &[data-resize-handle-state="drag"]:after {
    opacity: 1;
  }
`;

const ArchiveScreenContent = memo(() => {
  const { displayRouteSidebar, displayFilterSidebar, setDisplayRouteSidebar } = useArchiveUiStore();
  const selectedRoutesCount = useRoutesTableStore((state) => state.selectedRouteUuids.size);

  useEffect(() => {
    if (selectedRoutesCount) {
      setDisplayRouteSidebar(true);
    } else {
      setDisplayRouteSidebar(false);
    }
  }, [selectedRoutesCount, setDisplayRouteSidebar]);

  return (
    <>
      <OrientationModal />
      <Stack direction="row" height={"100dvh"}>
        <PanelGroup direction="vertical" style={{ flex: 1 }}>
          <Panel>
            <ArchiveGraphs />
          </Panel>
          <StyledPanelResizeHandleVertical />
          <Panel>
            <ArchiveRoutesTable />
          </Panel>
        </PanelGroup>
        <Box
          sx={{
            width: "350px",
            display: displayRouteSidebar || displayFilterSidebar ? "block" : "none",
          }}
        >
          {/* keep the route detail sidebar mounted so we keep the form state */}
          <ArchiveRoutesSidebar hidden={displayFilterSidebar} />
          {displayFilterSidebar ? <ArchiveFilterSidebar /> : null}
        </Box>
      </Stack>
      {/* <ContextMenu /> */}
    </>
  );
});

ArchiveScreenContent.displayName = "RoutesScreenContent";

const ArchiveFacetsProvider = ({ children }: { children: React.ReactNode }) => {
  const venueUuid = useCurrentVenueUuid();
  const climbType = useCurrentClimbType();
  const filterParams = useRoutesTableStore((state) => state.filterParams);
  const [debouncedFilterParams] = useDebounce(filterParams, 500);
  const apiFilterParams = useMemo(
    () => convertArchiveSortFilterParamsToBackend(debouncedFilterParams, []),
    [debouncedFilterParams],
  );

  const { data: facets } = useVenueArchiveRoutesFacetsQuery(
    venueUuid,
    climbType,
    apiFilterParams,
    apiFacetsToRoutesFacets,
  );
  return <RouteFacetsProvider value={facets}>{children}</RouteFacetsProvider>;
};

const flattenQueryData = (data: InfiniteData<PaginatedResponse<ApiRouteDetail>>) => {
  return {
    routes: data.pages.flatMap((page) => page.data),
    count: data.pages[0]?.metadata.count,
  };
};

const ArchiveQuery = ({ children }: { children: React.ReactNode }) => {
  const venueUuid = useCurrentVenueUuid();
  const climbType = useCurrentClimbType();
  const filterParams = useRoutesTableStore((state) => state.filterParams);
  const setFilterParam = useRoutesTableStore((state) => state.setFilterParam);
  const sortState = useArchiveUiStore((state) => state.sortState);
  const [hasSetDefault, setHasSetDefault] = useState(false);
  const [debouncedFilterParams] = useDebounce(filterParams, hasSetDefault ? 500 : 0);

  useEffect(() => {
    setFilterParam("endTime", {
      min: subYears(new Date(), 1).toISOString(),
    });
    setHasSetDefault(true);
    // oxlint-disable-next-line react/exhaustive-deps
  }, []);

  const apiFilterParams = useMemo(
    () => convertArchiveSortFilterParamsToBackend(debouncedFilterParams, sortState),
    [debouncedFilterParams, sortState],
  );
  const query = useVenueArchiveRoutesByClimbTypeQuery(
    // disable the query if we haven't set the default filter
    hasSetDefault ? venueUuid : undefined,
    climbType,
    apiFilterParams,
    flattenQueryData,
  );
  return <ArchiveQueryProvider query={query}>{children}</ArchiveQueryProvider>;
};
